import React from 'react';
import { motion } from 'framer-motion';

const TrustSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const features = [
    {
      icon: '🔒',
      title: 'Bank-Level Security',
      description: 'Advanced encryption protocols protect your data 24/7',
    },
    {
      icon: '⚡',
      title: 'Simple Onboarding',
      description: 'A streamlined process that gets you started quickly',
    },
    {
      icon: '✅',
      title: '99.9% Uptime',
      description: 'Industry-leading reliability and performance',
    },
    {
      icon: '🛡️',
      title: 'Secure Operations',
      description: 'Built to keep your account activity protected and stable',
    },
    {
      icon: '📊',
      title: 'Clear Reporting',
      description: 'Helpful account visibility and status updates at a glance',
    },
    {
      icon: '🤝',
      title: '24/7 Support',
      description: 'Dedicated support team ready to help anytime',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 border-y border-amber-500/20 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full filter blur-3xl opacity-20"
          animate={{ y: [0, 50, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/5 rounded-full filter blur-3xl opacity-20"
          animate={{ y: [0, -50, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Why Trust
            <span className="bg-gradient-to-r from-amber-400 to-amber-300 bg-clip-text text-transparent">
              {' '}Ceylon Broker?
            </span>
          </h2>
          <p className="text-gray-400 text-lg">
            Security, transparency, and reliability are at the core of everything we do
          </p>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/15 to-amber-500/5 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-all duration-500" />

              <div className="relative bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 border border-amber-500/25 group-hover:border-amber-500/50 transition-all duration-300 h-full">
                <motion.div
                  className="text-5xl mb-6"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                >
                  {feature.icon}
                </motion.div>

                <h3 className="text-xl font-bold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="mt-16 grid md:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {[
            { badge: '🔐', label: 'SSL Encryption' },
            { badge: '⚡', label: 'Fast Access' },
            { badge: '📈', label: 'Live Updates' },
            { badge: '🤝', label: 'Human Support' },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              className="text-center"
            >
              <motion.div
                className="text-4xl mb-3 mx-auto"
                whileHover={{ scale: 1.1 }}
              >
                {item.badge}
              </motion.div>
              <p className="text-sm text-amber-300 font-semibold">
                {item.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TrustSection;
